import { CheckIcon, CopyIcon } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { cn } from "~/lib/utils";

type CopiedValue = string | null;

type CopyFn = (text: string) => Promise<boolean>;

export function useCopyToClipboard(): [CopiedValue, CopyFn] {
  const [copiedText, setCopiedText] = useState<CopiedValue>(null);

  const copy: CopyFn = useCallback(async (text) => {
    if (!navigator?.clipboard) {
      console.warn("Clipboard not supported");
      return false;
    }

    // Try to save to clipboard then save it in the state if worked
    try {
      await navigator.clipboard.writeText(text);
      setCopiedText(text);
      return true;
    } catch (error) {
      console.warn("Copy failed", error);
      setCopiedText(null);
      return false;
    }
  }, []);

  return [copiedText, copy];
}

interface CopyToClipboardButtonProps {
  text: string;
  className?: string;
  label?: string;
}

export const CopyToClipboardButton = ({
  text,
  className,
  label = "Copy",
}: CopyToClipboardButtonProps) => {
  const [, copy] = useCopyToClipboard();

  const [isCopied, setIsCopied] = useState(false);

  useEffect(() => {
    if (!isCopied) return;

    const timeout = setTimeout(() => setIsCopied(false), 2000);
    return () => clearTimeout(timeout);
  }, [isCopied]);

  const handleCopy = async () => {
    const success = await copy(text);
    setIsCopied(success);
  };

  const Icon = isCopied ? CheckIcon : CopyIcon;

  return (
    <button
      type="button"
      onClick={handleCopy}
      className={cn("inline-flex items-center justify-center", className)}
      aria-label={isCopied ? `${label} copied` : label}
      title={isCopied ? `${label} copied` : label}
    >
      <Icon className={cn("h-3 w-3", isCopied && "text-green-500")} />
    </button>
  );
};

interface CopyToClipboardLabelProps {
  text: string;
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}

export const CopyToClipboardLabel = ({
  text,
  children,
  className,
  style,
}: CopyToClipboardLabelProps) => {
  const [, copy] = useCopyToClipboard();
  const [isCopied, setIsCopied] = useState(false);

  useEffect(() => {
    if (!isCopied) return;

    const timeout = setTimeout(() => setIsCopied(false), 2000);
    return () => clearTimeout(timeout);
  }, [isCopied]);

  const handleCopy = async () => {
    setIsCopied(await copy(text));
  };

  const Icon = isCopied ? CheckIcon : CopyIcon;

  return (
    <button
      type="button"
      onClick={handleCopy}
      className={cn("group cursor-pointer", className)}
      style={style}
      title={text}
    >
      {children}
      <Icon
        className={cn(
          "ml-2 inline h-3 w-3 opacity-0 transition-opacity group-hover:opacity-100",
          isCopied && "text-green-500 opacity-100",
        )}
      />
    </button>
  );
};
